import {
  FormControlLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  Typography,
  Divider,
  Box,
  Button,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import ArrowIcon from "@icons/myicons/arrow-down.svg?react";
import { BankAvatar, Banner } from "@components";
import { AccountTypeFromBankEnum } from "@bn/api/apiv1";
import { LoadingButton } from "../common/progress/LoadingButton";
import { CurrentDepot } from "./SelectContraAccount";

const Bank = ({
  bankName,
  bankImage,
  accountName,
  iban,
  type,
  accountNumber,
}: {
  bankName?: string;
  bankImage?: string;
  accountName?: string;
  iban?: string;
  type?: AccountTypeFromBankEnum;
  accountNumber?: string;
}) => {
  const { t } = useTranslation();
  const accountType = type
    ? t(`selectContraAccount.accountTypeFromBank.${type}`)
    : "";
  const details = [accountType || bankName, iban, accountNumber].filter(
    Boolean
  );

  return (
    <Box display="flex" alignItems="center" gap={3}>
      <BankAvatar imgUrl={bankImage} size={40} />
      <Box>
        <Typography variant="subtitle2">{accountName}</Typography>
        <Typography
          variant="body4"
          color="textSecondary"
          sx={{ wordBreak: "break-word" }}
        >
          {details.join(" • ")}
        </Typography>
      </Box>
    </Box>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: ${({ theme }) => theme.spacing(6)};

  ${({ theme }) => theme.breakpoints.down("md")} {
    gap: ${({ theme }) => theme.spacing(5)};
  }
`;

const RadioGroup = styled(MuiRadioGroup)`
  padding-left: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6.5)};
`;

export interface Option {
  id: number;
  accountName?: string;
  iban?: string;
  bankName?: string;
  bankImage?: string;
  type?: AccountTypeFromBankEnum;
}

export interface SelectContraAccountContainerProps {
  currentDepot: CurrentDepot;
  options: Option[];
  defaultId?: number;
  showNoAccountOption?: boolean;
  onSubmit: (id?: number) => void;
  isFetching?: boolean;
  showAccountsFromAllBanks?: boolean;
  onShowAccountsClicked?: () => void;
  bankName?: string;
  bankImage?: string;
}

export const SelectContraAccountContainer = ({
  currentDepot,
  options,
  defaultId,
  showNoAccountOption = false,
  onSubmit,
  showAccountsFromAllBanks = false,
  isFetching = false,
  onShowAccountsClicked,
}: SelectContraAccountContainerProps) => {
  const theme = useTheme();
  const [value, setValue] = useState(defaultId?.toString() || null);
  const { t } = useTranslation();

  const handleClick = () => {
    if (value === "no-value") {
      return onSubmit(undefined);
    }
    if (value) {
      return onSubmit(parseInt(value, 10));
    }
  };

  return (
    <Container data-testid="select-contra-account">
      <Typography variant="h4">{t("selectContraAccount.title")}</Typography>
      <Bank
        accountName={currentDepot.name}
        iban={currentDepot.iban}
        bankImage={currentDepot.bankImage}
        bankName={currentDepot.bankName}
        accountNumber={currentDepot.accountNumber}
      />
      <Divider
        sx={{
          width: "100%",
        }}
      />
      <RadioGroup
        aria-label="quiz"
        name="quiz"
        value={value}
        onChange={(_: React.SyntheticEvent, val: string) => {
          setValue(val);
        }}
      >
        {options.map(({ id, accountName, iban, bankImage, type }) => (
          <FormControlLabel
            key={id}
            value={id.toString()}
            control={<Radio data-testid={`radio-button-${id}`} />}
            label={
              <Box pl={4}>
                <Bank
                  accountName={accountName}
                  iban={iban}
                  bankImage={bankImage}
                  type={type}
                />
              </Box>
            }
          />
        ))}
        {showNoAccountOption && (
          <FormControlLabel
            key="no-account"
            value="no-value"
            control={<Radio data-testid="radio-button-no-account" />}
            label={
              <Box pl={4}>
                <Typography variant="subtitle2">
                  {t("selectContraAccount.noAccount")}
                </Typography>
              </Box>
            }
          />
        )}
      </RadioGroup>
      {onShowAccountsClicked && (
        <Button
          data-testid="show-accounts"
          color="primary"
          fullWidth
          variant="text"
          onClick={() => onShowAccountsClicked()}
          startIcon={<ArrowIcon />}
        >
          {showAccountsFromAllBanks
            ? t("selectContraAccount.showAccountsForSameBank")
            : t("selectContraAccount.showAllAccounts")}
        </Button>
      )}

      <Divider
        sx={{
          width: "100%",
        }}
      />

      {value === "no-value" && (
        <Box my={2} width="100%">
          <Banner
            data-testid="no-account-warning"
            type="warn"
            text={t("selectContraAccount.noAccountWarning")}
            displayDefaultIcon
          />
        </Box>
      )}

      <LoadingButton
        variant={theme.bankConnection?.button?.variant || "primary"}
        disabled={!value}
        fullWidth
        isFetching={isFetching}
        onClick={handleClick}
        data-testid="submit-button"
      >
        {t("selectContraAccount.nextButton")}
      </LoadingButton>
    </Container>
  );
};
